import type { LocalDataColumn } from "@nymbl/nos2-rules";
import { EXPENSE_APPROVALS_HELP, EXPENSES_HELP } from "@/lib/column-help";
import {
  APPROVAL_LABELS,
  type ApprovalStatus,
  PAYMENT_LABELS,
  type PaymentStatus,
} from "@/lib/expenses/rules";
import type { IsoDate } from "@/lib/time/dates";
import type { ExpenseApprovalRow, ExpenseClaimRow } from "@/schemas/expenses";

const MONEY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatMoney = (amount: number) => MONEY.format(amount);

const DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** "Dec 27, 2024": claims outlive their year, so the year always shows. */
export const formatClaimDate = (date: IsoDate) => DATE.format(new Date(`${date}T00:00:00Z`));

export const approvalLabel = (s: ApprovalStatus | null) => (s ? APPROVAL_LABELS[s] : "");
/** "Not approved" beside an Approved chip would contradict it: old claims can be in that state. */
export const paymentLabel = (s: PaymentStatus | null, approval: ApprovalStatus | null) => {
  if (s === "notapproved" && approval === "approved") return "Not scheduled";
  return s ? PAYMENT_LABELS[s] : "";
};

/** A pending claim nobody can decide reads as such everywhere (spec 7). */
export const NO_APPROVER = "No approver available";

export const EXPENSE_COLUMNS: LocalDataColumn<ExpenseClaimRow>[] = [
  {
    key: "submitted",
    label: "Submitted",
    type: "date",
    accessor: (c) => c.dateSubmitted,
    help: EXPENSES_HELP.submitted,
  },
  {
    key: "description",
    label: "Description",
    type: "text",
    accessor: (c) => c.description,
    searchText: (c) => [c.monthName, c.po].filter(Boolean).join(" "),
    help: EXPENSES_HELP.description,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (c) => c.typeName,
    help: EXPENSES_HELP.type,
  },
  {
    key: "approval",
    label: "Approval",
    type: "enum",
    accessor: (c) => c.approval,
    options: Object.entries(APPROVAL_LABELS).map(([value, label]) => ({ value, label })),
    help: EXPENSES_HELP.approval,
  },
  {
    key: "payment",
    label: "Payment",
    type: "enum",
    accessor: (c) => paymentLabel(c.payment, c.approval),
    help: EXPENSES_HELP.payment,
  },
  {
    key: "estPaid",
    label: "Est. payment",
    type: "date",
    accessor: (c) => c.estPaidOn,
    help: EXPENSES_HELP.estPaid,
  },
  {
    key: "amount",
    label: "Amount",
    type: "number",
    accessor: (c) => c.total,
    help: EXPENSES_HELP.amount,
  },
];

export const APPROVAL_COLUMNS: LocalDataColumn<ExpenseApprovalRow>[] = [
  {
    key: "person",
    label: "Person",
    type: "enum",
    accessor: (r) => r.claimantName,
    help: EXPENSE_APPROVALS_HELP.person,
  },
  {
    key: "description",
    label: "Description",
    type: "text",
    accessor: (r) => r.description,
    help: EXPENSE_APPROVALS_HELP.description,
  },
  {
    key: "type",
    label: "Type",
    type: "enum",
    accessor: (r) => r.typeName,
    help: EXPENSE_APPROVALS_HELP.type,
  },
  {
    key: "department",
    label: "Department",
    type: "enum",
    accessor: (r) => r.department,
    help: EXPENSE_APPROVALS_HELP.department,
  },
  {
    key: "po",
    label: "PO",
    type: "text",
    accessor: (r) => r.po,
    help: EXPENSE_APPROVALS_HELP.po,
  },
  {
    key: "month",
    label: "Month",
    type: "text",
    accessor: (r) => r.monthName,
    help: EXPENSE_APPROVALS_HELP.month,
  },
  {
    key: "submitted",
    label: "Submitted",
    type: "date",
    accessor: (r) => r.dateSubmitted,
    help: EXPENSE_APPROVALS_HELP.submitted,
  },
  {
    key: "amount",
    label: "Amount",
    type: "number",
    accessor: (r) => r.total,
    help: EXPENSE_APPROVALS_HELP.amount,
  },
];

/** Newest first; drafts (never submitted) on top. */
export function claimOrder(a: ExpenseClaimRow, b: ExpenseClaimRow): number {
  return (
    (b.dateSubmitted ?? "9999").localeCompare(a.dateSubmitted ?? "9999") || a.id.localeCompare(b.id)
  );
}
